import {
  AwaitContainer,
  Button,
  Checkbox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Item,
  ItemActions,
  ItemDescription,
  ItemHeader,
  ItemTitle,
  TextWithDescription,
} from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributeResponse, useAttributes } from '@/entities/attributes';

type Props = {
  existingAttributeCodes: string[];
  onCancel: () => void;
  onSave: (attributes: AttributeResponse[]) => void;
  className?: string;
};

export const AddAttributeDialog: FC<Props> = ({ existingAttributeCodes, onCancel, onSave, className }) => {
  const { t } = useTranslation(['common', 'attributeGroups']);

  const { data, isLoading } = useAttributes({ limit: 1000, 'is-deleted': false });

  const [search, setSearch] = useState('');
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);

  const handleToggle = (code: string) => {
    setSelectedCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const attributes: AttributeResponse[] = useMemo(() => {
    return data?.attributes?.filter((attr) => !existingAttributeCodes.includes(attr.code)) || [];
  }, [existingAttributeCodes, data?.attributes]);

  const filtered = useMemo(() => {
    if (!search.trim()) return attributes;
    const q = search.toLowerCase();
    return attributes.filter((a) => {
      return a.name.toLowerCase().includes(q) || a.code.toLowerCase().includes(q);
    });
  }, [attributes, search]);

  const handleSave = () => onSave(attributes.filter((attr) => selectedCodes.includes(attr.code)));

  return (
    <DialogContent className={className}>
      <DialogHeader>
        <DialogTitle>{t('attributeGroups:addAttribute')}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common:search')} />

        <AwaitContainer isLoading={isLoading}>
          <div className="flex flex-col gap-3 max-h-120 overflow-auto">
            {filtered.map((attr) => (
              <Item variant="outline" size={'sm'} key={attr.code}>
                <ItemHeader>
                  <ItemTitle>
                    <TextWithDescription
                      text={attr.name}
                      description={<span className="font-mono text-sm">{attr.code}</span>}
                    />
                  </ItemTitle>
                  <ItemActions className="ml-4">
                    <Checkbox value={selectedCodes.includes(attr.code)} onClick={() => handleToggle(attr.code)} />
                  </ItemActions>
                </ItemHeader>
                <ItemDescription className="text-sm text-muted-foreground line-clamp-1">
                  {attr.description}
                </ItemDescription>
              </Item>
            ))}
            {filtered.length === 0 && (
              <div className="text-muted-foreground">{t('attributeGroups:noAttributesFound')}</div>
            )}
          </div>
        </AwaitContainer>
      </div>

      <DialogFooter>
        <div className="flex gap-3 justify-end w-full">
          <Button variant="ghost" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSave}>{t('common:add')}</Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};
